// Client-side mirror of the room: players, the shared loop, stats.

import { cellAlive, cellKey, type Cell, type Narration, type PlayerInfo, type ServerHome, type ServerMsg, type Stats } from "../shared/protocol";
import { Emitter } from "./emitter";

export interface RoomEvents extends Record<string, unknown> {
  welcome: void;
  players: void;
  join: PlayerInfo;
  leave: PlayerInfo;
  cell: { step: number; lane: number; cell: Cell | null; prev: Cell | null; by: PlayerInfo | null; at: number };
  pluck: Extract<ServerMsg, { t: "pluck" }>;
  cursor: Extract<ServerMsg, { t: "cursor" }>;
  stats: Stats;
  error: string;
  narration: Narration;
  rtt: { id: string; ms: number };
}

export class Room extends Emitter<RoomEvents> {
  name = "earth";
  you: PlayerInfo | null = null;
  players = new Map<string, PlayerInfo>();
  cells = new Map<number, Cell>();
  home: ServerHome | null = null;
  stats: Stats | null = null;
  epoch = 0;
  /** Ids of players who left recently, so their notes keep a name. */
  private departed = new Map<string, PlayerInfo>();

  apply(msg: ServerMsg): void {
    switch (msg.t) {
      case "welcome": {
        this.name = msg.room;
        this.you = msg.you;
        this.epoch = msg.epoch;
        this.home = msg.home;
        this.stats = msg.stats;
        this.players = new Map(msg.players.map((p) => [p.id, p]));
        this.cells = new Map(msg.cells.map((c) => [cellKey(c.step, c.lane), c]));
        this.emit("welcome", undefined);
        this.emit("players", undefined);
        if (msg.narration) this.emit("narration", msg.narration);
        return;
      }
      case "join": {
        if (!this.you) return;
        const existed = this.players.has(msg.player.id);
        this.players.set(msg.player.id, msg.player);
        if (msg.player.id === this.you.id) this.you = msg.player;
        if (!existed) this.emit("join", msg.player);
        this.emit("players", undefined);
        return;
      }
      case "leave": {
        if (!this.you) return;
        const p = this.players.get(msg.id);
        if (!p) return;
        this.players.delete(msg.id);
        this.departed.set(p.id, p);
        this.emit("leave", p);
        this.emit("players", undefined);
        return;
      }
      case "cell": {
        if (!this.you) return;
        const k = cellKey(msg.step, msg.lane);
        const prev = this.cells.get(k) ?? null;
        if (msg.cell) {
          this.cells.set(k, msg.cell);
          // Keep the counter lively between the server's periodic stats.
          if (this.stats && !this.player(msg.by)?.ghost) this.stats.notesEver++;
        } else this.cells.delete(k);
        this.emit("cell", { step: msg.step, lane: msg.lane, cell: msg.cell, prev, by: this.player(msg.by), at: msg.at });
        return;
      }
      case "pluck":
        if (!this.you) return;
        if (this.stats) this.stats.notesEver++;
        this.emit("pluck", msg);
        return;
      case "cursor":
        if (this.you) this.emit("cursor", msg);
        return;
      case "stats":
        this.stats = msg.stats;
        this.emit("stats", msg.stats);
        return;
      case "rtt": {
        const p = this.players.get(msg.id);
        if (p) p.rtt = msg.ms;
        this.emit("rtt", { id: msg.id, ms: msg.ms });
        return;
      }
      case "narration":
        this.emit("narration", { text: msg.text, by: msg.by, at: msg.at });
        return;
      case "error":
        this.emit("error", msg.message);
        return;
      case "pong":
        return;
    }
  }

  player(id: string): PlayerInfo | null {
    return this.players.get(id) ?? this.departed.get(id) ?? null;
  }

  /** Drops cells whose time has passed (the server does the same). */
  prune(bar: number): void {
    for (const [k, c] of this.cells) if (!cellAlive(c, bar)) this.cells.delete(k);
  }

  humans(): PlayerInfo[] {
    return [...this.players.values()].filter((p) => !p.ghost);
  }

  cellsAtStep(step: number): Cell[] {
    const out: Cell[] = [];
    for (const c of this.cells.values()) if (c.step === step) out.push(c);
    return out;
  }
}
